import React, { useEffect, useState } from "react";
import {
  limitToLast,
  onValue,
  orderByChild,
  push,
  query,
  ref,
  remove,
  serverTimestamp,
} from "firebase/database";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import { filterText } from "../utils/profanityFilter";

const COMMENTABLE_TYPES = ["playlists", "forge", "frames"];
const MAX_COMMENT_LENGTH = 1000;
const MAX_VISIBLE_COMMENTS = 100;

export default function CommentSection({ type, postId }) {
  const { t, i18n } = useTranslation();
  const { user, userData, isAdmin } = useAuth();
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [actionError, setActionError] = useState("");

  useEffect(() => {
    if (!postId || !COMMENTABLE_TYPES.includes(type)) {
      setComments([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError("");
    const commentsQuery = query(
      ref(db, `comments/${type}/${postId}`),
      orderByChild("createdAt"),
      limitToLast(MAX_VISIBLE_COMMENTS)
    );

    return onValue(
      commentsQuery,
      (snapshot) => {
        const records = snapshot.exists()
          ? Object.entries(snapshot.val())
              .map(([id, comment]) => ({ id, ...comment }))
              .filter(comment => typeof comment.text === "string" && typeof comment.uid === "string" && Number.isFinite(comment.createdAt))
              .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
          : [];
        setComments(records);
        setLoading(false);
      },
      () => {
        setError(t("comments.loadError"));
        setLoading(false);
      }
    );
  }, [postId, t, type]);

  async function handleSubmit(event) {
    event.preventDefault();
    const text = filterText(draft.trim()).trim();
    if (!user || !postId || !COMMENTABLE_TYPES.includes(type) || submitting) return;
    if (!text || text.length > MAX_COMMENT_LENGTH) {
      setActionError(t("comments.invalid"));
      return;
    }

    setSubmitting(true);
    setActionError("");
    try {
      await push(ref(db, `comments/${type}/${postId}`), {
        uid: user.uid,
        text,
        createdAt: serverTimestamp(),
      });
      setDraft("");
    } catch {
      setActionError(t("comments.saveError"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(commentId) {
    setDeletingId(commentId);
    setActionError("");
    try {
      await remove(ref(db, `comments/${type}/${postId}/${commentId}`));
    } catch {
      setActionError(t("comments.deleteError"));
    } finally {
      setDeletingId("");
    }
  }

  function formatDate(timestamp) {
    const date = new Date(timestamp);
    if (!Number.isFinite(date.getTime())) return "";
    const locale = i18n.language?.startsWith("tr") ? "tr-TR" : "en-US";
    return date.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
  }

  return (
    <section
      aria-label={t("comments.title")}
      style={{
        marginTop: 18,
        padding: "16px 18px",
        background: "var(--bone-dark)",
        border: "1px solid var(--bone-gold-border)",
        borderRadius: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14 }}>
        <h3 style={{
          margin: 0,
          color: "var(--bone-text-primary)",
          fontFamily: "'Playfair Display',serif",
          fontSize: 16,
        }}>{t("comments.title")}</h3>
        <span style={{ color: "var(--bone-text-muted)", fontSize: 10 }}>
          {t("comments.count", { count: comments.length })}
        </span>
      </div>

      {loading ? (
        <p style={{ color: "var(--bone-text-secondary)", fontSize: 12 }}>{t("common.loading")}</p>
      ) : error ? (
        <p role="alert" style={{ color: "#e05c5c", fontSize: 12 }}>{error}</p>
      ) : comments.length === 0 ? (
        <p style={{ color: "var(--bone-text-secondary)", fontSize: 12 }}>{t("comments.empty")}</p>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {comments.map(comment => {
            const isOwner = comment.uid === user?.uid;
            const canDelete = isOwner || isAdmin;
            const author = isOwner
              ? userData?.displayName || user.displayName || t("comments.you")
              : `${t("comments.member")} · ${comment.uid.slice(0, 8)}`;

            return (
              <li key={comment.id} style={{
                padding: "12px 0",
                borderTop: "1px solid var(--bone-gold-faint)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  <strong style={{ color: "var(--bone-text-accent)", fontSize: 11 }}>{author}</strong>
                  <time dateTime={new Date(comment.createdAt).toISOString()} style={{ color: "var(--bone-text-muted)", fontSize: 10 }}>
                    {formatDate(comment.createdAt)}
                  </time>
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => handleDelete(comment.id)}
                      disabled={deletingId === comment.id}
                      aria-label={t("comments.delete")}
                      style={{
                        marginLeft: "auto",
                        padding: "4px 8px",
                        border: "1px solid var(--bone-gold-border)",
                        borderRadius: 6,
                        background: "transparent",
                        color: "var(--bone-text-secondary)",
                        cursor: deletingId === comment.id ? "wait" : "pointer",
                      }}
                    >{deletingId === comment.id ? "…" : t("comments.delete")}</button>
                  )}
                </div>
                <p style={{
                  margin: 0,
                  color: "var(--bone-text-secondary)",
                  fontSize: 13,
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                }}>{comment.text}</p>
              </li>
            );
          })}
        </ul>
      )}

      {user ? (
        <form onSubmit={handleSubmit} style={{ marginTop: 14 }}>
          <label htmlFor={`comment-${type}-${postId}`} style={{
            display: "block",
            marginBottom: 6,
            color: "var(--bone-text-secondary)",
            fontSize: 11,
          }}>{t("comments.label")}</label>
          <textarea
            id={`comment-${type}-${postId}`}
            value={draft}
            onChange={event => { setDraft(event.target.value); setActionError(""); }}
            maxLength={MAX_COMMENT_LENGTH}
            rows={3}
            placeholder={t("comments.placeholder")}
            style={{
              display: "block",
              width: "100%",
              boxSizing: "border-box",
              padding: 10,
              resize: "vertical",
              border: "1px solid var(--bone-gold-border)",
              borderRadius: 6,
              background: "var(--bone-card)",
              color: "var(--bone-text-primary)",
              font: "inherit",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 8 }}>
            <span aria-live="polite" style={{ color: actionError ? "#e05c5c" : "var(--bone-text-muted)", fontSize: 10 }}>
              {actionError || `${draft.length}/${MAX_COMMENT_LENGTH}`}
            </span>
            <button
              type="submit"
              disabled={submitting || !draft.trim()}
              style={{
                padding: "8px 14px",
                border: "1px solid var(--bone-gold-border)",
                borderRadius: 6,
                background: "rgba(212,168,83,0.12)",
                color: "var(--bone-text-accent)",
                cursor: submitting || !draft.trim() ? "not-allowed" : "pointer",
                opacity: submitting || !draft.trim() ? 0.6 : 1,
              }}
            >{submitting ? t("comments.posting") : t("comments.post")}</button>
          </div>
        </form>
      ) : (
        <p style={{ margin: "14px 0 0", color: "var(--bone-text-muted)", fontSize: 11 }}>
          {t("comments.loginRequired")}
        </p>
      )}
    </section>
  );
}
